/**
 * UserSearchCombobox — "email or username" input with username
 * autocomplete against the platform user directory (GET /users/search).
 *
 * A controlled free-text field: typing a non-email string of ≥2
 * characters shows debounced username-prefix suggestions; typing an
 * email address (any "@" past the first character) suppresses them so
 * the field doubles as a plain email input. Selecting a suggestion
 * fills the field with `@username` — the parent form derives the
 * invite payload from the final text (see parseInviteRecipient in
 * lib/vault-invitations.ts), so no separate "selected user" state is
 * needed.
 *
 * Structure follows ContactCombobox (plain input + positioned list)
 * with keyboard navigation and the combobox ARIA pattern from chat's
 * MentionList added on top. The list preventDefaults mousedown so the
 * input never loses focus while picking — closing is then simply
 * "input blurred", with no timers to race (a press-and-drag-off leaves
 * the field focused with the list open, the standard combobox
 * behavior).
 */

import { useId, useState } from 'react';
import type { UserSearchResultDto } from '@/lib/api';
import { isEmailLikeRecipient } from '@/lib/vault-invitations';
import { useUserSearch } from '@/hooks/useUserSearch';

interface UserSearchComboboxProps {
  /** Controlled free text — an email address or a (possibly @-prefixed) username. */
  value: string;
  onValueChange: (value: string) => void;
  /** A suggestion was picked (also fires onValueChange with `@username`). */
  onSelectUser?: (user: UserSearchResultDto) => void;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  id?: string;
  ariaLabel?: string;
  className?: string;
}

export function UserSearchCombobox({
  value,
  onValueChange,
  onSelectUser,
  placeholder = 'teammate@example.com or username',
  disabled,
  required,
  id,
  ariaLabel = 'Email or username',
  className,
}: UserSearchComboboxProps) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const listId = useId();

  const trimmed = value.trim().replace(/^@/, '');
  const searching = !isEmailLikeRecipient(value) && trimmed.length >= 2;
  const { data: users = [], isFetching, isDebouncing } = useUserSearch(value);

  const showList = open && searching;
  // Results can shrink under a background refetch — never let the
  // active index (or aria-activedescendant) point past the list.
  const safeActiveIndex = activeIndex < users.length ? activeIndex : -1;

  const close = () => {
    setOpen(false);
    setActiveIndex(-1);
  };

  const choose = (user: UserSearchResultDto) => {
    onValueChange(`@${user.username}`);
    onSelectUser?.(user);
    close();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showList || users.length === 0) {
      if (e.key === 'Escape') close();
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % users.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => (i <= 0 ? users.length - 1 : i - 1));
    } else if (e.key === 'Enter' && safeActiveIndex >= 0) {
      // Only intercept Enter while a suggestion is highlighted — plain
      // Enter falls through and submits the surrounding form with the
      // typed text (the server resolves it).
      e.preventDefault();
      choose(users[safeActiveIndex]);
    } else if (e.key === 'Escape') {
      close();
    }
  };

  return (
    <div className="relative">
      <input
        type="text"
        id={id}
        value={value}
        disabled={disabled}
        required={required}
        placeholder={placeholder}
        maxLength={320}
        autoComplete="off"
        role="combobox"
        aria-label={ariaLabel}
        aria-autocomplete="list"
        aria-expanded={showList}
        aria-controls={listId}
        aria-activedescendant={
          showList && safeActiveIndex >= 0 ? `${listId}-option-${safeActiveIndex}` : undefined
        }
        onChange={(e) => {
          onValueChange(e.target.value);
          setOpen(true);
          setActiveIndex(-1);
        }}
        onFocus={() => setOpen(true)}
        onBlur={close}
        onKeyDown={handleKeyDown}
        className={
          className ?? 'w-full rounded border border-border bg-background px-3 py-2 text-sm'
        }
      />
      {showList && users.length > 0 && (
        <ul
          id={listId}
          role="listbox"
          aria-label="Matching users"
          className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded border border-border bg-card shadow-lg"
          // Keep focus in the input while interacting with the list, so
          // onBlur (the only close path besides Escape/selection) can't
          // fire mid-click.
          onMouseDown={(e) => e.preventDefault()}
        >
          {users.map((user, index) => (
            <li
              key={user.username}
              id={`${listId}-option-${index}`}
              role="option"
              aria-selected={index === safeActiveIndex}
            >
              <button
                type="button"
                tabIndex={-1}
                onClick={() => choose(user)}
                onMouseEnter={() => setActiveIndex(index)}
                className={`block w-full px-3 py-2 text-left text-sm ${
                  index === safeActiveIndex ? 'bg-muted/40' : 'hover:bg-muted/40'
                }`}
              >
                <span className="font-medium">{user.displayName}</span>
                <span className="ml-2 text-xs text-muted-foreground">@{user.username}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {showList && users.length === 0 && (
        <div className="absolute z-10 mt-1 w-full rounded border border-border bg-card px-3 py-2 text-sm text-muted-foreground shadow-lg">
          {isFetching || isDebouncing
            ? 'Searching…'
            : 'No matching users — enter an email to invite by email.'}
        </div>
      )}
    </div>
  );
}
